import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import {
  ResourceView,
  filterToCondition,
  type ActiveFiltersState,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
  type FilterMenuOutput,
} from "@/shared/components/ResourceView";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { FormattedDateTime } from "@/shared/datetime";
import {
  groupTruncationOf,
  type MetricSortField,
  type NumericAggregation,
  type Period,
} from "@/shared/domain/metrics";
import { useInfiniteQueryMetrics } from "@/shared/domain/metrics/hooks";
import { groupKeyLabel } from "@/shared/domain/query/notSet";
import { Button } from "@/shared/foundation/primitives/Button";
import { Spinner } from "@/shared/foundation/primitives/Spinner";
import { type Updater } from "@/shared/foundation/primitives/Table";
import { useMeasure } from "@/shared/hooks";
import { Goto } from "@/shared/navigation/Goto";
import { nanoSecToDate, nsToIso } from "@/shared/time/time";
import type { Condition } from "@/types";

import { groupKeysToFilters } from "../dashboardFilters/groupKeyFilter";
import {
  DASHBOARD_PARAMS,
  mergeParams,
  readRange,
  writeRange,
} from "../DashboardHeader/DashboardParams";

import { withBucketCounts } from "./bucketCounts";
import { BucketWindow, bucketToQueryRequest } from "./bucketWindow";
import styles from "./DrillDownDialog.module.css";
import { getDrillDownTitle } from "./drillDownTitle";
import { GROUP_CAP_EXPLANATION, hiddenGroupsText } from "./groupCap";
import { type SeriesCounts, withSeriesSuggestions } from "./seriesSuggestions";
import { useBucketCounts } from "./useBucketCounts";

export interface DrillDownDialogProps {
  open: boolean;
  onClose: () => void;
  metricName: string;
  period: Period;
  /** The clicked chart bucket, or `null` when nothing is selected. */
  bucket: BucketWindow | null;
  /** Names the window in the dialog's title, replacing the period-derived
   *  bucket label. Supplied by the RAW card, whose "bucket" is a single
   *  instant a nanosecond wide — "March 31, 2026" would describe a calendar
   *  day it never asked for. */
  windowLabel?: string;
  orgId: string | undefined;
  deviceIds?: string[];
  /** The dashboard's session scope. */
  sessionIds?: string[];
  /** The sessions a click on several points resolved, all within
   *  `sessionIds`. They describe the series the click seeded the filters
   *  with, so they narrow the table only while its group-by filter still
   *  selects those series: once the reader edits or removes it, the table
   *  falls back to `sessionIds`. */
  clickedSessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** The dashboard's filter vocabulary. Absent, the dialog offers no filters
   *  of its own. */
  filterDefinitions?: FilterDefinition[];
  /** The card's group-by path, when it is grouped. */
  groupBy?: string;
  /** The group values of the series under the clicked bucket (`null` for the
   *  "field unset" series). Seeds the dialog's filters with exactly those
   *  series; `undefined` seeds none. */
  groupKeys?: (string | null)[];
  /** Every series the card's chart draws (`null` for the "Not set" one), in
   *  legend order. Suggested first when editing the group-by filter, so the
   *  reader can widen or swap the clicked series without typing it. */
  seriesKeys?: (string | null)[];
  /** Each series' sessions among a raw card's clicked points: the rows the
   *  table lists before the reader filters it. Shown beside each suggested
   *  series, which are ranked by it. An aggregated card counts every row
   *  through the aggregate endpoint instead (see `aggregation`). */
  seriesCounts?: SeriesCounts;
  /** The aggregated card's aggregation. Its bucket is a whole period, which
   *  the aggregate endpoint can count by the value of any field it can split
   *  by, so each such row's values are counted in the bucket under the
   *  dialog's other filters (see `withBucketCounts`). A raw card leaves it
   *  out: its window can't be counted, so only its group-by row shows counts,
   *  the clicked points'. */
  aggregation?: NumericAggregation;
}

interface DrillDownSort {
  sortBy: MetricSortField;
  descending: boolean;
}

/** Session end, oldest first: the server's own default order. */
const DEFAULT_SORT: DrillDownSort = { sortBy: "time", descending: false };

const DEVICE_GROUP_BY = "device.device_id";

/** What the reader has changed about one drilled selection's table. */
interface DrillDownView {
  sort: DrillDownSort;
  filters: Filter[];
  matchMode: FilterMatchMode;
}

/** Every column of the records table, each sortable server-side. */
const COLUMNS: {
  label: string;
  sortBy: MetricSortField;
  align?: "right";
}[] = [
  // The drill-down filters on session end (`time_filter: "end_time"`), so the
  // default "time" sort orders by exactly this column.
  { label: "Session end", sortBy: "time" },
  { label: "Published", sortBy: "published" },
  { label: "Value", sortBy: "value", align: "right" },
  { label: "Device", sortBy: "device_id" },
  { label: "Session", sortBy: "session_id" },
  { label: "Invocation", sortBy: "invocation_id" },
];

/**
 * Stands in for the active column's sort arrow while a re-sorted first page
 * loads. Hidden from assistive technology: the header cell carries
 * `aria-busy` instead, so the sort button keeps its name.
 */
function SortingSpinner({ className }: { className?: string }) {
  return <Spinner aria-hidden className={className} />;
}

/**
 * Modal that lists the raw metric records inside a single clicked window: an
 * aggregate bucket from an aggregated card, or the one-nanosecond instant
 * behind a single point on a raw card. The query is gated on `open` so
 * nothing is fetched until the user drills in.
 */
export function DrillDownDialog({
  open,
  onClose,
  metricName,
  period,
  bucket,
  windowLabel,
  orgId,
  deviceIds,
  sessionIds,
  clickedSessionIds,
  condition,
  filterDefinitions: dashboardFilterDefinitions,
  groupBy,
  groupKeys,
  seriesKeys,
  seriesCounts,
  aggregation,
}: DrillDownDialogProps) {
  // The group-by row suggests the chart's own series ahead of its org-wide
  // search. Its chips' values are not the source, so a series removed from
  // the chip is offered again.
  const seriesDefinitions = useMemo(
    () =>
      withSeriesSuggestions(
        dashboardFilterDefinitions,
        groupBy,
        seriesKeys,
        seriesCounts,
      ),
    [dashboardFilterDefinitions, groupBy, seriesKeys, seriesCounts],
  );
  // The sort and filters belong to one drilled selection: drilling into
  // another (or closing, which clears `bucket`) starts back at the defaults.
  // Tagging the state with the selection it was set for, rather than resetting
  // it in an effect, means a stale view is never rendered or requested even
  // once.
  const selectionKey = bucket
    ? [
        metricName,
        bucket.start_time,
        bucket.end_time,
        sessionIds?.join(","),
        clickedSessionIds?.join(","),
        JSON.stringify(groupKeys),
      ].join("|")
    : null;
  const seeded = seedFilters(seriesDefinitions, groupBy, groupKeys);
  const [viewState, setViewState] = useState<{
    selectionKey: string | null;
    view: DrillDownView;
  } | null>(null);
  const view: DrillDownView =
    viewState?.selectionKey === selectionKey
      ? viewState.view
      : {
          sort: DEFAULT_SORT,
          filters: seeded,
          matchMode: "AND",
        };
  const { sort, filters, matchMode } = view;
  const updateView = (next: Partial<DrillDownView>) =>
    setViewState({ selectionKey, view: { ...view, ...next } });

  const onSortClick = (sortBy: MetricSortField) => {
    // A new column starts ascending; the active column flips direction.
    const next =
      sort.sortBy === sortBy
        ? { sortBy, descending: !sort.descending }
        : { sortBy, descending: false };
    updateView({ sort: next });
  };

  // Filter edits keep the sort, and re-sorting keeps the filters: the two are
  // independent views of the same selection.
  const onFilterSelected = (output: FilterMenuOutput) => {
    if (output.type === "structured") {
      updateView({ filters: [...filters, output.filter] });
    }
  };
  const onFilterRemove = (index: number) =>
    updateView({ filters: filters.filter((_, i) => i !== index) });
  const onFilterUpdate = (index: number, filter: Filter) =>
    updateView({ filters: filters.map((f, i) => (i === index ? filter : f)) });
  const onMatchModeChange = (updater: Updater<FilterMatchMode>) =>
    updateView({
      matchMode: typeof updater === "function" ? updater(matchMode) : updater,
    });
  // Each row counts the rows the table would list with a value ticked, where
  // the window can be counted, and shows no counts where it can't.
  const countsFor = useBucketCounts({
    metricName,
    period,
    aggregation,
    bucket,
    orgId,
    deviceIds,
    sessionIds,
    condition,
    filters,
    matchMode,
    definitions: seriesDefinitions,
  });
  const filterDefinitions = useMemo(
    () => withBucketCounts(seriesDefinitions, { groupBy, countsFor }),
    [seriesDefinitions, groupBy, countsFor],
  );
  // The series each row belongs to, as its own column — except when the
  // series IS the device, which the Device column already names.
  const showGroupColumn = groupBy !== undefined && groupBy !== DEVICE_GROUP_BY;
  // Labels the group column as the chart's legend labels its series.
  const groupByType = dashboardFilterDefinitions?.find(
    (d) => d.field === groupBy,
  )?.type;

  const activeFilters: ActiveFiltersState =
    filters.length > 0
      ? { type: "structured", filters, matchMode }
      : { type: "none" };

  const isDefaultSort =
    sort.sortBy === DEFAULT_SORT.sortBy &&
    sort.descending === DEFAULT_SORT.descending;
  // The clicked sessions belong to the clicked series: kept on another series
  // they would hide its rows, and kept with no series filter they would hide
  // every series but the clicked one.
  const scopedSessionIds =
    clickedSessionIds !== undefined &&
    seriesSelection(filters, groupBy) === seriesSelection(seeded, groupBy)
      ? clickedSessionIds
      : sessionIds;
  const request =
    open && bucket
      ? bucketToQueryRequest({
          name: metricName,
          bucket,
          deviceIds,
          sessionIds: scopedSessionIds,
          condition,
          // A filter change is a new request and so a new query, paginating
          // from page one: a page token is bound to its sort but not to its
          // filters, so continuing one under another filter would be wrong.
          drillCondition: filterToCondition(filters, {
            matchMode,
            definitions: seriesDefinitions,
          }),
          groupBy,
          // Left off in the default order, which the server applies anyway, so
          // an unsorted drill-down sends the same request it always has.
          ...(!isDefaultSort && sort),
        })
      : undefined;
  // The sort is part of the request, and so of the query key: a new sort is a
  // new query that paginates from page one, as the server requires (a page
  // token is bound to the sort that issued it).
  const {
    data,
    isLoading,
    isPlaceholderData,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQueryMetrics(request, orgId, {
    // Keep the current rows on screen while a re-sorted first page loads.
    keepPagesAcrossSortChange: true,
  });
  // Any other change (a filter, a new bucket) is a new query that starts
  // empty, so while its first page loads the records hold the height they last
  // settled at: the dialog stays put around the spinner instead of collapsing
  // and springing back open when the rows land. The first load has nothing to
  // hold, and once rows (or "No records") arrive the region fits them again.
  // The server caps a grouped query's groups, the drill-down's included: rows
  // of groups past the cap never arrive, so the table says so. Every page
  // reports the same cap for the whole query.
  const groupTruncation = groupTruncationOf(data?.pages.at(-1));
  const [recordsRef, recordsSize] = useMeasure<HTMLDivElement>();
  const [settledHeight, setSettledHeight] = useState(0);
  if (!isLoading && recordsSize.height !== settledHeight) {
    setSettledHeight(recordsSize.height);
  }
  const holdHeight = isLoading && settledHeight > 0;

  const title = (() => {
    if (windowLabel) {
      return windowLabel;
    } else if (bucket) {
      return getDrillDownTitle(
        // Display only — date-resolution title, so the lossy ns→number is fine.
        nsToIso(Number(bucket.start_time)),
        nsToIso(Number(bucket.end_time)),
        period,
      );
    } else {
      return "";
    }
  })();
  const records = data?.pages.flatMap((p) => p.items) ?? [];
  // Placeholder rows only ever stand in across a sort change on the same
  // selection, so while they show, the clicked column's new order is loading.
  const isSorting = isPlaceholderData;

  // Carry the dashboard's current time range + period onto the device metrics
  // tab (which renders the same MetricsDashboard and reads these params) so the
  // drill-down lands on a matching view. Period comes via prop; range lives in
  // the URL alongside it.
  const [searchParams] = useSearchParams();
  // Built through writeRange so a custom range carries its `start`/`end`
  // bounds across too — a bare `range=custom` would not survive the trip.
  const deviceViewParams = mergeParams(new URLSearchParams(), {
    ...writeRange(readRange(searchParams)),
    [DASHBOARD_PARAMS.period]: period,
  }).toString();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <div className={styles.titleRow}>
          <span className={styles.titleText}>
            {metricName}
            {title ? ` — ${title}` : ""}
          </span>
          {/* With nothing filtered, the add-filter button rides in the title
              bar: a filter row holding only a button would just take up
              space. Once a chip shows, the chip bar's own add button takes
              over, so there is never a second one. */}
          {filterDefinitions && filters.length === 0 && (
            <ResourceView.Filters
              filterDefinitions={filterDefinitions}
              onFilterSelected={onFilterSelected}
              metrics={false}
            />
          )}
          <IconButton aria-label="close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers className={styles.content}>
        {filterDefinitions && filters.length > 0 && (
          <div className={styles.filterBar}>
            <ResourceView.ActiveFilters
              filters={activeFilters}
              filterDefinitions={filterDefinitions}
              onFilterSelected={onFilterSelected}
              onRemove={onFilterRemove}
              onUpdate={onFilterUpdate}
              onClear={() => updateView({ filters: [] })}
              onMatchModeChange={onMatchModeChange}
              metrics={false}
            />
          </div>
        )}
        {groupTruncation && (
          <p className={styles.groupCapNote} role="note">
            {hiddenGroupsText(groupTruncation.hiddenGroups)}.{" "}
            {GROUP_CAP_EXPLANATION}
          </p>
        )}
        <div
          ref={recordsRef}
          role="region"
          aria-label="Records"
          aria-busy={isLoading}
          className={classNames({ [styles.recordsHeld]: holdHeight })}
          style={holdHeight ? { minHeight: settledHeight } : undefined}
        >
          {isLoading && (
            <div className={styles.state}>
              <CircularProgress size={24} />
            </div>
          )}
          {error && (
            <Alert severity="error">
              Failed to load records: {error.message}
            </Alert>
          )}
          {!isLoading && !error && records.length === 0 && (
            <div className={styles.state}>
              {filters.length > 0
                ? "No records in this window match these filters."
                : "No records in this window."}
            </div>
          )}
          {!isLoading && !error && records.length > 0 && (
            <>
              <TableContainer className={styles.tableScroll}>
                <Table size="small" stickyHeader aria-label="metric records">
                  <TableHead>
                    <TableRow>
                      {showGroupColumn && (
                        // The header is the group-by path, as the filter
                        // chips name the same field. It sorts on the query's
                        // own `group_by`, which is why the request carries it.
                        <TableCell
                          sortDirection={
                            sort.sortBy === "group_key"
                              ? sortDirectionOf(sort)
                              : false
                          }
                          aria-busy={
                            (sort.sortBy === "group_key" && isSorting) ||
                            undefined
                          }
                        >
                          <TableSortLabel
                            active={sort.sortBy === "group_key"}
                            direction={
                              sort.sortBy === "group_key"
                                ? sortDirectionOf(sort)
                                : "asc"
                            }
                            onClick={() => onSortClick("group_key")}
                            IconComponent={
                              sort.sortBy === "group_key" && isSorting
                                ? SortingSpinner
                                : undefined
                            }
                          >
                            {groupBy}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {COLUMNS.map((column) => {
                        const active = sort.sortBy === column.sortBy;
                        const direction = sort.descending ? "desc" : "asc";
                        const busy = active && isSorting;
                        return (
                          <TableCell
                            key={column.sortBy}
                            align={column.align}
                            sortDirection={active ? direction : false}
                            aria-busy={busy || undefined}
                          >
                            {/* MUI's sort label used directly, like the rest of
                              this table: `@/shared/foundation` has no plain
                              table primitive to hang one on (its DataTable is
                              AG Grid, paged by page number, with no keyset
                              "Load more"), and a lone sort label would be a
                              wrapper around a table this file already takes
                              from MUI. */}
                            <TableSortLabel
                              active={active}
                              direction={active ? direction : "asc"}
                              onClick={() => onSortClick(column.sortBy)}
                              IconComponent={busy ? SortingSpinner : undefined}
                            >
                              {column.label}
                            </TableSortLabel>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((r) => (
                      // A metric has one record per session, so the session id
                      // is unique within the table.
                      <TableRow key={r.session_id}>
                        {showGroupColumn && (
                          <TableCell>
                            {groupKeyLabel(r.group_key ?? null, groupByType)}
                          </TableCell>
                        )}
                        <TableCell>
                          <FormattedDateTime
                            value={
                              r.max_timestamp_ns === null
                                ? null
                                : nanoSecToDate(r.max_timestamp_ns)
                            }
                            fallback="—"
                          />
                        </TableCell>
                        <TableCell>
                          <FormattedDateTime value={r.published} />
                        </TableCell>
                        <TableCell align="right">{r.value}</TableCell>
                        <TableCell>
                          {r.device_id ? (
                            // Land on the device's Metrics tab (not the default
                            // Datasets tab) — the user is drilling into metrics.
                            // Deliberately the BARE tab (no dashboard id): the
                            // active dashboard is org-scoped view state, and
                            // carrying it across would change behavior. A new
                            // tab, so the drilled table stays open here.
                            <RobotoLinkHoverUnderline
                              to={`${Goto.createDeviceMetricsRoute(r.device_id)}?${deviceViewParams}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {r.device_id}
                            </RobotoLinkHoverUnderline>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Land on the session's Metrics tab with this metric
                            preselected, so the tree opens expanded to it and
                            its detail card is shown, in a new tab so the drilled
                            table stays open here. The "metrics" segment and
                            "selectedMetric" param are inlined rather than
                            imported from the Sessions feature (features must not
                            import from one another). */}
                          <RobotoLinkHoverUnderline
                            to={`${Goto.route.Sessions}/${r.session_id}/metrics?selectedMetric=${encodeURIComponent(
                              metricName,
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {r.session_id}
                          </RobotoLinkHoverUnderline>
                        </TableCell>
                        <TableCell>{r.invocation_id ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {/* Hidden while a re-sort's rows stand in for its first page:
                  their next page belongs to the old sort. */}
                {hasNextPage && !isPlaceholderData && (
                  <div className={styles.loadMore}>
                    <Button
                      variant="text"
                      size="small"
                      loading={isFetchingNextPage}
                      onClick={() => void fetchNextPage()}
                    >
                      Load more
                    </Button>
                  </div>
                )}
              </TableContainer>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The filters a drill-down opens with: the series under the clicked bucket of
 * a grouped card, as ordinary chips the reader can edit, or remove to widen
 * the table to every series. None on an ungrouped card, or when the group-by
 * field is not among the dashboard's filter fields (a custom field deleted
 * since the card was saved), where the table falls back to every series in
 * the window.
 */
function seedFilters(
  filterDefinitions: FilterDefinition[] | undefined,
  groupBy: string | undefined,
  groupKeys: (string | null)[] | undefined,
): Filter[] {
  const definition = filterDefinitions?.find((d) => d.field === groupBy);
  const seeded =
    definition && groupKeys && groupKeysToFilters(definition, groupKeys);
  return seeded ?? [];
}

/**
 * The series `filters` select: their rows on the group-by field, by what each
 * matches rather than how it was built, so two selections compare equal when
 * they name the same series.
 */
function seriesSelection(
  filters: readonly Filter[],
  groupBy: string | undefined,
): string {
  return JSON.stringify(
    filters
      .filter((filter) => filter.field === groupBy)
      .map((filter) => [
        filter.comparator,
        (filter.values as readonly unknown[]).map(optionValue).sort(),
        "includeNull" in filter && filter.includeNull === true,
      ]),
  );
}

/** A row value's wire form: a labeled option's `value`, else the value itself. */
function optionValue(value: unknown): string {
  return String(
    typeof value === "object" && value !== null && "value" in value
      ? value.value
      : value,
  );
}

function sortDirectionOf(sort: DrillDownSort): "asc" | "desc" {
  return sort.descending ? "desc" : "asc";
}
